import {
  Cancel,
  CheckCircle,
  Delete,
  Person,
  Schedule,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../../../services/redux/store';
import DeleteApprovalDialog from './DeleteApprovalDialog';
import { ApprovalHistoryCardProps } from './types';

const ApprovalHistoryCard = ({
  approval,
  index,
  onDeleteApproval,
  canDeleteApproval,
}: ApprovalHistoryCardProps) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Aprobada':
        return 'success';
      case 'Rechazada':
        return 'error';
      default:
        return 'warning';
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async (approvalId: number) => {
    if (onDeleteApproval) {
      await onDeleteApproval(approvalId);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  return (
    <>
      <Paper
        key={index}
        sx={{
          p: 2,
          mb: 2,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'nowrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: '30%',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              backgroundColor:
                approval.status === 'Aprobada'
                  ? 'rgba(46, 125, 50, 0.15)'
                  : 'rgba(237, 108, 2, 0.15)',
              borderRadius: '50%',
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {approval.status === 'Aprobada' ? (
              <CheckCircle sx={{ fontSize: '1.5rem', color: 'success.main' }} />
            ) : approval.status === 'Rechazada' ? (
              <Cancel sx={{ fontSize: '1.5rem', color: 'error.main' }} />
            ) : (
              <Schedule sx={{ fontSize: '1.5rem', color: 'warning.main' }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                lineHeight: 1,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              {approval.user}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1,
              }}
            >
              {approval.userEmail}
            </Typography>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Chip
          label={approval.status}
          color={getStatusColor(approval.status)}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            flexShrink: 0,
          }}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: 'rgba(128, 128, 128, 0.05)',
              p: 0.75,
              borderRadius: '50%',
              mr: 1,
            }}
          >
            <Person sx={{ fontSize: '0.95rem', color: 'text.secondary' }} />
          </Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1 }}
          >
            {approval.role}
          </Typography>
        </Box>

        {approval.reason && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 120,
                flex: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  lineHeight: 1,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                &ldquo;{approval.reason}&rdquo;
              </Typography>
            </Box>
          </>
        )}

        {canDeleteApproval &&
          onDeleteApproval &&
          approval.userId === currentUser?.id && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Tooltip title="Eliminar aprobación" arrow>
                  <IconButton
                    onClick={handleDeleteClick}
                    sx={{
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.2)',
                      },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <Delete sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
      </Paper>

      {openDeleteDialog && (
        <DeleteApprovalDialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          approval={approval}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}
    </>
  );
};

export default ApprovalHistoryCard;
